import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useSession } from '@supabase/auth-helpers-react';
import { supabase } from '../lib/supabase';
import SEO from '../components/SEO';
import MarkdownRenderer from '../components/MarkdownRenderer';
import FAQAccordion from '../components/FAQAccordion';
import AdminActionsBar from '../components/AdminActionsBar';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import { CampDiary } from '../types/database';
import { CalendarIcon, UserIcon, TagIcon, ChevronRightIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

export default function CampDiariesPage() {
  const { slug } = useParams<{ slug?: string }>();
  const [searchParams] = useSearchParams();
  const tag = searchParams.get('tag');

  // If we have a slug, show individual diary entry
  if (slug) {
    return <CampDiaryDetail slug={slug} />;
  }

  // Otherwise show list of diaries
  return <CampDiariesList tag={tag} />;
}

function CampDiariesList({ tag }: { tag?: string | null }) {
  const [page, setPage] = useState(1);
  const perPage = 12;
  const session = useSession();

  const { data: diaries, isLoading, error } = useQuery(
    ['camp-diaries', page, tag],
    async () => {
      let query = supabase
        .from('camp_diaries')
        .select('*')
        .eq('published', true)
        .order('published_at', { ascending: false })
        .range((page - 1) * perPage, page * perPage - 1);

      if (tag) {
        query = query.contains('tags', [tag]);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as CampDiary[];
    },
    {
      staleTime: 1000 * 60 * 5, // 5 minutes
    }
  );

  const { data: allTags } = useQuery(
    ['camp-diary-tags'],
    async () => {
      const { data, error } = await supabase
        .from('camp_diaries')
        .select('tags')
        .eq('published', true)
        .not('tags', 'is', null);
      
      if (error) throw error;
      
      const tagSet = new Set<string>();
      data.forEach(diary => {
        if (diary.tags) {
          diary.tags.forEach((t: string) => tagSet.add(t));
        }
      });
      return Array.from(tagSet).sort();
    },
    {
      staleTime: 1000 * 60 * 10, // 10 minutes
    }
  );

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="page-content">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="page-content">
          <div className="text-center py-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Unable to load camp diaries</h2>
            <p className="text-gray-600">Please try again later.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title={tag ? `Camp Diaries: ${tag} | CampWhiz` : "Camp Diaries | CampWhiz"}
        description={tag 
          ? `Discover inspiring camp stories and experiences tagged with ${tag}. Read real camper adventures and parent perspectives.`
          : "Insights and ideas for parents and camp owners — from summer planning tips and child development insights to strategies for boosting visibility and engagement."
        }
        url={tag ? `/camp-diaries?tag=${tag}` : "/camp-diaries"}
      />
      
      <div className="page-container">
        <div className="page-content">
          <div className="max-w-4xl mx-auto">
            <header className="mb-8 text-center">
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold text-gray-900 mb-4">
                {tag ? `Camp Diaries: ${tag}` : "Camp Diaries"}
              </h1>
              <p className="text-xl text-gray-600">
                {tag 
                  ? `Stories and experiences tagged with "${tag}"`
                  : "Insights and ideas for parents and camp owners — from summer planning tips and child development insights to strategies for boosting visibility and engagement."
                }
              </p>
            </header>

            {/* Tags Filter */}
            {allTags && allTags.length > 0 && (
              <div className="mb-8 flex flex-wrap gap-2 justify-center">
                <Link
                  to="/camp-diaries"
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors border-2 ${
                    !tag
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white text-primary border-primary hover:bg-primary/5'
                  }`}
                >
                  All Stories
                </Link>
                {allTags.map((t) => (
                  <Link
                    key={t}
                    to={`/camp-diaries?tag=${encodeURIComponent(t)}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors border-2 ${
                      tag === t
                        ? 'bg-primary text-white border-primary'
                        : 'bg-white text-primary border-primary hover:bg-primary/5'
                    }`}
                  >
                    {t}
                  </Link>
                ))}
              </div>
            )}

            {/* Diaries Grid */}
            {diaries && diaries.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {diaries.map((diary) => (
                  <Link
                    key={diary.id}
                    to={`/camp-diaries/${diary.slug}`}
                    className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow overflow-hidden border border-gray-200"
                  >
                    {diary.featured_image_url && (
                      <img
                        src={diary.featured_image_url}
                        alt={diary.title}
                        className="w-full aspect-video object-contain bg-gray-100"
                      />
                    )}
                    <div className="p-6">
                      <h2 className="text-xl font-semibold text-gray-900 mb-2 line-clamp-2">
                        {diary.title}
                      </h2>
                    {diary.excerpt && (
                      <p className="text-gray-600 mb-4 line-clamp-3">{diary.excerpt}</p>
                    )}
                    <div className="flex items-center text-sm text-gray-500">
                      {diary.author_name && (
                        <>
                          <UserIcon className="h-4 w-4 mr-1" />
                          <span className="mr-4">{diary.author_name}</span>
                        </>
                      )}
                      {diary.published_at && (
                        <>
                          <CalendarIcon className="h-4 w-4 mr-1" />
                          <span>{new Date(diary.published_at).toLocaleDateString()}</span>
                        </>
                      )}
                    </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-600">No camp diaries found.</p>
              </div>
            )}

            {/* Pagination */}
            {diaries && diaries.length === perPage && (
              <div className="flex justify-center gap-4">
              {page > 1 && (
                <button
                  onClick={() => setPage(page - 1)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                >
                  Previous
                </button>
              )}
              <button
                onClick={() => setPage(page + 1)}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors"
              >
                Next
              </button>
            </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * Extract FAQ schema JSON from content if present
 * According to Google: https://developers.google.com/search/docs/appearance/structured-data/faqpage
 * 
 * Supports the default schema.org FAQPage format:
 * {
 *   "@context": "https://schema.org",
 *   "@type": "FAQPage",
 *   "mainEntity": [
 *     {
 *       "@type": "Question",
 *       "name": "Question text",
 *       "acceptedAnswer": {
 *         "@type": "Answer",
 *         "text": "Answer text (can contain HTML)"
 *       }
 *     }
 *   ]
 * }
 * 
 * The JSON can appear anywhere in the content and may have markdown escaping (like \[ instead of [)
 * The function will:
 * 1. Extract the FAQPage JSON from the content
 * 2. Replace it with a placeholder that will be rendered as a styled FAQ component
 * 3. Validate and clean the schema
 * 4. Return both the content (with placeholder) and the FAQ schema for structured data
 * 
 * The placeholder format: <!-- FAQ_COMPONENT_PLACEHOLDER -->
 * This will be replaced with the actual FAQ accordion component during rendering
 */
function extractFAQSchema(content: string): { cleanedContent: string; faqSchema: any | null; faqItems: any[] | null } {
  // Safety checks
  if (!content || typeof content !== 'string') {
    return { cleanedContent: content || '', faqSchema: null, faqItems: null };
  }
  
  // Limit content size to prevent performance issues (max 1MB)
  if (content.length > 1000000) {
    console.warn('⚠️ [FAQ-EXTRACT] Content too large, skipping extraction');
    return { cleanedContent: content, faqSchema: null, faqItems: null };
  }
  
  console.log('🔍 [FAQ-EXTRACT] Starting extraction, content length:', content.length);
  
  // Strategy: Search for FAQPage pattern anywhere in content
  // The JSON might have escaped brackets from markdown (like \[ instead of [)
  // Try multiple patterns to catch different formats
  let faqPageMatchIndex = -1;
  
  // Pattern 1: Standard "@type":"FAQPage"
  const pattern1 = /"@type"\s*:\s*"FAQPage"/i;
  faqPageMatchIndex = content.search(pattern1);
  
  // Pattern 2: With escaped quotes or different spacing
  if (faqPageMatchIndex === -1) {
    const pattern2 = /@type[\s"]*:\s*[\s"]*FAQPage/i;
    faqPageMatchIndex = content.search(pattern2);
  }
  
  // Pattern 3: Just "FAQPage" (less specific but might catch it)
  if (faqPageMatchIndex === -1) {
    const pattern3 = /"FAQPage"/i;
    faqPageMatchIndex = content.search(pattern3);
  }
  
  if (faqPageMatchIndex === -1) {
    console.log('⚠️ [FAQ-EXTRACT] No FAQPage pattern found in content');
    // Log a sample of the content to help debug
    console.log('⚠️ [FAQ-EXTRACT] Content sample (last 500 chars):', content.substring(Math.max(0, content.length - 500)));
    return { cleanedContent: content, faqSchema: null, faqItems: null };
  }
  
  console.log('🔍 [FAQ-EXTRACT] Found FAQPage pattern at position:', faqPageMatchIndex);
  console.log('🔍 [FAQ-EXTRACT] Context around match:', content.substring(Math.max(0, faqPageMatchIndex - 50), Math.min(content.length, faqPageMatchIndex + 100)));
  
  // Find the opening brace before FAQPage - look backwards up to 3000 chars
  // This allows for longer JSON structures
  let jsonStart = -1;
  for (let i = faqPageMatchIndex; i >= 0 && i >= faqPageMatchIndex - 3000; i--) {
    if (content[i] === '{') {
      jsonStart = i;
      break;
    }
  }
  
  if (jsonStart === -1) {
    console.log('⚠️ [FAQ-EXTRACT] Could not find opening brace before FAQPage');
    console.log('⚠️ [FAQ-EXTRACT] Searched', faqPageMatchIndex, 'characters backwards');
    return { cleanedContent: content, faqSchema: null, faqItems: null };
  }
  
  console.log('🔍 [FAQ-EXTRACT] Found opening brace at position:', jsonStart);
  console.log('🔍 [FAQ-EXTRACT] Distance from FAQPage match:', faqPageMatchIndex - jsonStart, 'characters');
  
  // Find the matching closing brace by counting braces
  // We need to properly handle escaped characters and strings
  let braceCount = 0;
  let inString = false;
  let escapeNext = false;
  let jsonEnd = -1;
  
  // Limit the search to prevent infinite loops or performance issues
  const maxSearchLength = Math.min(content.length, jsonStart + 50000); // Increased limit but still bounded
  
  try {
    for (let i = jsonStart; i < maxSearchLength; i++) {
      const char = content[i];
      
      if (escapeNext) {
        escapeNext = false;
        continue;
      }
      
      if (char === '\\') {
        escapeNext = true;
        continue;
      }
      
      if (char === '"' && !escapeNext) {
        inString = !inString;
        continue;
      }
      
      if (!inString) {
        if (char === '{') {
          braceCount++;
        } else if (char === '}') {
          braceCount--;
          if (braceCount === 0) {
            jsonEnd = i + 1;
            break;
          }
        }
      }
    }
  } catch (error) {
    console.error('❌ [FAQ-EXTRACT] Error finding closing brace:', error);
    return { cleanedContent: content, faqSchema: null, faqItems: null };
  }
  
  if (jsonEnd === -1 || jsonEnd <= jsonStart) {
    console.log('⚠️ [FAQ-EXTRACT] Could not find matching closing brace');
    return { cleanedContent: content, faqSchema: null, faqItems: null };
  }
  
  console.log('🔍 [FAQ-EXTRACT] Found closing brace at position:', jsonEnd);
  
  // Extract the JSON string (may contain markdown escapes)
  let jsonString = content.substring(jsonStart, jsonEnd);
  const originalJsonString = jsonString;
  console.log('🔍 [FAQ-EXTRACT] Extracted JSON string, length:', jsonString.length);
  console.log('🔍 [FAQ-EXTRACT] JSON preview (first 200 chars):', jsonString.substring(0, 200));
  
  // Try to parse the JSON - first as-is, then with unescaping
  let parsed: any = null;
  
  try {
    // First try parsing as-is
    parsed = JSON.parse(jsonString);
    console.log('✅ [FAQ-EXTRACT] Successfully parsed JSON as-is');
  } catch (parseError) {
    console.log('🔍 [FAQ-EXTRACT] First parse failed, trying to unescape markdown...');
    console.log('🔍 [FAQ-EXTRACT] Parse error:', parseError instanceof Error ? parseError.message : parseError);
    
    // Unescape markdown-style escapes
    // The JSON might have markdown escapes like \[ \] which need to be converted to [ ]
    // We need to do this carefully to avoid breaking valid JSON escape sequences
    let unescaped = jsonString;
    
    // Check if the JSON has literal backslash-bracket sequences (markdown escapes)
    if (jsonString.includes('\\[') || jsonString.includes('\\]')) {
      console.log('🔍 [FAQ-EXTRACT] Found escaped brackets, attempting to fix...');
      
      // Strategy: Replace \\[ with [ and \\] with ]
      // But we need to be careful - in JSON, a backslash before [ or ] is not a valid escape
      // So if we see \\[ or \\], it's likely a markdown escape that should be [ or ]
      // However, we need to avoid replacing escaped quotes and other valid JSON escapes
      
      // Simple approach: Replace all \\[ with [ and \\] with ]
      // This works because in valid JSON, you wouldn't have \\[ or \\] - 
      // you'd have just [ or ] for arrays, or \\" for escaped quotes
      unescaped = jsonString
        .replace(/\\\[/g, '[')   // Replace \[ with [
        .replace(/\\\]/g, ']');   // Replace \] with ]
      
      console.log('🔍 [FAQ-EXTRACT] After unescaping brackets, length:', unescaped.length);
    }
    
    try {
      parsed = JSON.parse(unescaped);
      console.log('✅ [FAQ-EXTRACT] Successfully parsed after unescaping');
      jsonString = unescaped; // Update for removal
    } catch (secondError) {
      console.warn('⚠️ [FAQ-EXTRACT] Parse failed even after unescaping');
      if (secondError instanceof Error) {
        console.warn('⚠️ [FAQ-EXTRACT] Second error:', secondError.message);
        console.warn('⚠️ [FAQ-EXTRACT] Error at position:', (secondError as any).toString());
      }
      
      // Last attempt: try to fix common JSON issues
      try {
        // Remove any control characters that might break parsing
        let fixed = unescaped.replace(/[\u0000-\u001F\u007F-\u009F]/g, '');
        // For now, skip the complex escape fixing - just try to parse
        // If it fails, we'll return the original content
        parsed = JSON.parse(fixed);
        console.log('✅ [FAQ-EXTRACT] Successfully parsed after removing control characters');
        jsonString = fixed;
      } catch (thirdError) {
        console.error('❌ [FAQ-EXTRACT] All parsing attempts failed');
        console.error('❌ [FAQ-EXTRACT] Original JSON (first 500 chars):', originalJsonString.substring(0, 500));
        console.error('❌ [FAQ-EXTRACT] Unescaped JSON (first 500 chars):', unescaped.substring(0, 500));
        return { cleanedContent: content, faqSchema: null, faqItems: null };
      }
    }
  }
  
  // Validate it's a FAQPage schema
  if (!parsed || parsed['@type'] !== 'FAQPage') {
    console.log('⚠️ [FAQ-EXTRACT] JSON is not a FAQPage schema');
    console.log('⚠️ [FAQ-EXTRACT] Type:', parsed?.['@type']);
    return { cleanedContent: content, faqSchema: null, faqItems: null };
  }
  
  // Validate context
  if (parsed['@context'] !== 'https://schema.org') {
    console.log('⚠️ [FAQ-EXTRACT] Invalid context:', parsed['@context']);
    // Still accept it if it's close or missing (Google allows this)
    if (parsed['@context'] && parsed['@context'] !== 'https://schema.org') {
      console.log('⚠️ [FAQ-EXTRACT] Fixing context to https://schema.org');
      parsed['@context'] = 'https://schema.org';
    }
  }
  
  // Validate structure according to Google's requirements
  if (!parsed.mainEntity || !Array.isArray(parsed.mainEntity) || parsed.mainEntity.length === 0) {
    console.warn('⚠️ [FAQ-EXTRACT] FAQPage missing mainEntity array');
    return { cleanedContent: content, faqSchema: null, faqItems: null };
  }
  
  // Validate each question has required fields per Google's spec
  // Also check content quality best practices
  const validQuestions = parsed.mainEntity.filter((q: any) => {
    // Basic structure validation
    const hasValidStructure = q['@type'] === 'Question' && 
                    q.name && 
                    typeof q.name === 'string' &&
                    q.name.trim().length > 0 &&
                    q.acceptedAnswer && 
                    q.acceptedAnswer['@type'] === 'Answer' && 
                    q.acceptedAnswer.text &&
                    typeof q.acceptedAnswer.text === 'string' &&
                    q.acceptedAnswer.text.trim().length > 0;
    
    if (!hasValidStructure) {
      console.warn('⚠️ [FAQ-EXTRACT] Invalid question structure:', {
        hasType: !!q['@type'],
        type: q['@type'],
        hasName: !!q.name,
        nameLength: q.name?.length || 0,
        hasAcceptedAnswer: !!q.acceptedAnswer,
        answerType: q.acceptedAnswer?.['@type'],
        hasText: !!q.acceptedAnswer?.text,
        answerLength: q.acceptedAnswer?.text?.length || 0
      });
      return false;
    }
    
    // Content quality checks (warnings, not blockers)
    const answerText = q.acceptedAnswer.text.replace(/<[^>]*>/g, ' ').trim();
    const sentenceCount = (answerText.match(/[.!?]+/g) || []).length;
    
    // Best practice: Answers should be 1-3 sentences (warning only)
    if (sentenceCount > 3) {
      console.warn(`⚠️ [FAQ-EXTRACT] Answer for "${q.name.substring(0, 50)}..." is ${sentenceCount} sentences. Best practice: 1-3 sentences for concise answers.`);
    }
    
    // Check if answer is too short (might not be helpful)
    if (answerText.length < 20) {
      console.warn(`⚠️ [FAQ-EXTRACT] Answer for "${q.name.substring(0, 50)}..." is very short (${answerText.length} chars). Ensure it fully answers the question.`);
    }
    
    // Check if question is too long (should be concise)
    if (q.name.length > 150) {
      console.warn(`⚠️ [FAQ-EXTRACT] Question is very long (${q.name.length} chars). Best practice: Keep questions concise and focused.`);
    }
    
    return true;
  });
  
  if (validQuestions.length === 0) {
    console.warn('⚠️ [FAQ-EXTRACT] No valid questions found in FAQPage');
    return { cleanedContent: content, faqSchema: null, faqItems: null };
  }
  
  console.log('✅ [FAQ-EXTRACT] Found', validQuestions.length, 'valid FAQ questions out of', parsed.mainEntity.length);
  
  // Replace the JSON with a placeholder that will be rendered as the FAQ component
  // This preserves the position in the content while removing the raw JSON
  const FAQ_PLACEHOLDER = '<!-- FAQ_COMPONENT_PLACEHOLDER -->';
  
  // Get the content before and after the JSON
  let beforeJson = content.substring(0, jsonStart);
  let afterJson = content.substring(jsonEnd);
  
  // Clean whitespace around the JSON
  // Remove trailing whitespace from before (including newlines)
  beforeJson = beforeJson.replace(/[\s\n\r]*$/, '');
  
  // Remove leading whitespace from after (including newlines)  
  afterJson = afterJson.replace(/^[\s\n\r]*/, '');
  
  // If there's a blank line before the JSON, keep one newline for spacing
  beforeJson = beforeJson.replace(/\n{2,}$/, '\n');
  
  // Replace JSON with placeholder - this will be rendered as the FAQ component
  let cleanedContent = (beforeJson + '\n\n' + FAQ_PLACEHOLDER + '\n\n' + afterJson).trim();
  
  // Double-check: verify the JSON is actually replaced with placeholder
  // If we still see FAQPage JSON, try to replace it with placeholder
  if (cleanedContent.includes('"FAQPage"') && !cleanedContent.includes(FAQ_PLACEHOLDER)) {
    console.warn('⚠️ [FAQ-EXTRACT] WARNING: JSON still appears in cleaned content!');
    console.warn('⚠️ [FAQ-EXTRACT] Attempting to replace with placeholder...');
    
    // Try to find and replace any remaining FAQPage JSON with placeholder
    const faqPageIndex = cleanedContent.indexOf('"FAQPage"');
    if (faqPageIndex !== -1) {
      // Find the brace before it
      let fallbackStart = -1;
      for (let i = faqPageIndex; i >= 0 && i >= faqPageIndex - 2000; i--) {
        if (cleanedContent[i] === '{') {
          fallbackStart = i;
          break;
        }
      }
      
      if (fallbackStart !== -1) {
        // Find the matching closing brace
        let fallbackBraceCount = 0;
        let fallbackInString = false;
        let fallbackEscapeNext = false;
        let fallbackEnd = -1;
        
        for (let i = fallbackStart; i < cleanedContent.length && i < fallbackStart + 20000; i++) {
          const char = cleanedContent[i];
          if (fallbackEscapeNext) {
            fallbackEscapeNext = false;
            continue;
          }
          if (char === '\\') {
            fallbackEscapeNext = true;
            continue;
          }
          if (char === '"' && !fallbackEscapeNext) {
            fallbackInString = !fallbackInString;
            continue;
          }
          if (!fallbackInString) {
            if (char === '{') fallbackBraceCount++;
            if (char === '}') {
              fallbackBraceCount--;
              if (fallbackBraceCount === 0) {
                fallbackEnd = i + 1;
                break;
              }
            }
          }
        }
        
        if (fallbackEnd > fallbackStart) {
          const beforeFallback = cleanedContent.substring(0, fallbackStart).replace(/[\s\n\r]*$/, '');
          const afterFallback = cleanedContent.substring(fallbackEnd).replace(/^[\s\n\r]*/, '');
          cleanedContent = (beforeFallback + '\n\n' + FAQ_PLACEHOLDER + '\n\n' + afterFallback).trim();
          console.log('✅ [FAQ-EXTRACT] Replaced JSON with placeholder in fallback cleanup');
        }
      }
    }
  }
  
  console.log('✅ [FAQ-EXTRACT] Successfully extracted and removed FAQ schema');
  console.log('✅ [FAQ-EXTRACT] Original length:', content.length);
  console.log('✅ [FAQ-EXTRACT] Cleaned length:', cleanedContent.length);
  console.log('✅ [FAQ-EXTRACT] Removed', content.length - cleanedContent.length, 'characters');
  
  // Create a clean FAQ schema object with only valid questions
  // Ensure it matches Google's requirements exactly
  // Best Practices:
  // - Use JSON-LD format (done via SEO component)
  // - Place in <head> (done via SEO component)
  // - Content must match visible content exactly (ensured by FAQAccordion component)
  // - One answer per question (validated above)
  const cleanFAQSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: validQuestions.map((q: any) => ({
      '@type': 'Question',
      name: q.name.trim(), // Ensure no leading/trailing whitespace
      acceptedAnswer: {
        '@type': 'Answer',
        text: q.acceptedAnswer.text.trim() // Ensure no leading/trailing whitespace
      }
    }))
  };
  
  // Log validation summary
  console.log('✅ [FAQ-EXTRACT] FAQ Schema Validation Summary:');
  console.log(`   - Total questions: ${validQuestions.length}`);
  console.log(`   - All questions have required fields: ✅`);
  console.log(`   - Schema format: JSON-LD ✅`);
  console.log(`   - Schema type: FAQPage ✅`);
  console.log(`   - Content will be visible on page: ✅ (via FAQAccordion component)`);
  console.log(`   ⚠️  Remember to validate with Google Rich Results Test: https://search.google.com/test/rich-results`);
  
  // Return the FAQ items for rendering the component
  const faqItems = validQuestions.map((q: any) => ({
    '@type': 'Question' as const,
    name: q.name,
    acceptedAnswer: {
      '@type': 'Answer' as const,
      text: q.acceptedAnswer.text
    }
  }));
  
  return { cleanedContent, faqSchema: cleanFAQSchema, faqItems };
}

/**
 * Component that renders content and replaces FAQ placeholders with the FAQ accordion component
 */
function FAQContentRenderer({ content, faqItems }: { content: string; faqItems: any[] | null }) {
  const FAQ_PLACEHOLDER = '<!-- FAQ_COMPONENT_PLACEHOLDER -->';
  
  if (!content) {
    return null;
  }
  
  // If there's no FAQ placeholder or no FAQ items, just render the content normally
  if (!content.includes(FAQ_PLACEHOLDER) || !faqItems || faqItems.length === 0) {
    return <MarkdownRenderer content={content} />;
  }
  
  // Split content by the placeholder
  const parts = content.split(FAQ_PLACEHOLDER);
  
  return (
    <>
      {parts.map((part, index) => (
        <React.Fragment key={index}>
          {part && <MarkdownRenderer content={part} />}
          {index < parts.length - 1 && (
            <div className="my-8">
              <FAQAccordion faqs={faqItems} />
            </div>
          )}
        </React.Fragment>
      ))}
    </>
  );
}

function CampDiaryDetail({ slug }: { slug: string }) {
  console.log('🔍 [CAMP-DIARY-DETAIL] Rendering component for slug:', slug);
  console.log('🔍 [CAMP-DIARY-DETAIL] Is server-side rendering:', typeof window === 'undefined');
  
  const session = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deleteConfirmation, setDeleteConfirmation] = useState<CampDiary | null>(null);
  
  // Track if component has mounted to prevent hydration mismatches
  // This ensures SSR and client render the same initial data
  const [hasMounted, setHasMounted] = useState(false);
  const isSSR = typeof window === 'undefined';
  
  useEffect(() => {
    setHasMounted(true);
  }, []);
  
  // Check admin status FIRST - needed for query logic
  // During SSR, always use false (no session available)
  const { data: isAdmin } = useQuery(
    ['is-admin', session?.user?.id],
    async () => {
      if (!session?.user?.id) return false;
      
      const { data, error } = await supabase
        .rpc('check_is_admin', {
          user_id: session.user.id
        });
      
      if (error) {
        console.error('Error checking admin status:', error);
        return false;
      }
      
      return data || false;
    },
    {
      enabled: !!session?.user?.id && !isSSR, // Disable during SSR
      staleTime: 1000 * 60 * 5, // 5 minutes
      // Default to false for non-logged-in users and SSR
      initialData: false,
    }
  );

  // SIMPLIFIED SSR APPROACH:
  // During SSR, use a simple key without adminStatus
  // After client hydration, we can use adminStatus if needed
  // This prevents hydration mismatches
  const adminStatus = (hasMounted && !isSSR) ? (isAdmin ?? false) : false;

  // During SSR, always use the simple ['camp-diary', slug] key
  // This matches what entry-server.tsx seeds
  const queryKey = isSSR ? ['camp-diary', slug] : ['camp-diary', slug, adminStatus];

  const { data: diary, isLoading, error } = useQuery(
    queryKey,
    async () => {
      if (isSSR) {
        console.log('🔍 [CAMP-DIARY-DETAIL SSR] Query function called during SSR - this should not happen!');
      }

      let query = supabase
        .from('camp_diaries')
        .select('*')
        .eq('slug', slug);

      // Only filter by published if user is NOT an admin
      if (!adminStatus) {
        query = query.eq('published', true);
      }

      const { data, error } = await query.single();

      if (error) throw error;
      return data as CampDiary;
    },
    {
      staleTime: 1000 * 60 * 10, // 10 minutes
    }
  );

  // Get the actual diary data - during SSR, get from query client directly
  // This ensures we have data for server-side rendering
  const actualDiary = (() => {
    // During SSR, get data directly from query client (pre-fetched in entry-server.tsx)
    // This bypasses useQuery which might not return data synchronously during SSR
    if (isSSR) {
      const ssrData = queryClient.getQueryData(['camp-diary', slug]) as CampDiary | undefined;
      if (ssrData) {
        console.log('🔍 [CAMP-DIARY-DETAIL SSR] Found SSR data from query client:', ssrData.title);
        return ssrData;
      } else {
        console.warn('🔍 [CAMP-DIARY-DETAIL SSR] No SSR data found in query client for slug:', slug);
        return undefined;
      }
    }

    // On client-side, use the data from useQuery
    return diary;
  })();

  // Extract FAQ schema using useMemo - MUST be called before any early returns
  // This ensures hooks are always called in the same order
  // Use safe defaults to prevent errors
  const { cleanedContent, faqSchema, faqItems } = useMemo(() => {
    try {
      // Safety check: if no diary or no content, return empty defaults
      if (!actualDiary || !actualDiary.content) {
        return { cleanedContent: '', faqSchema: null, faqItems: null };
      }
      
      // Extract FAQ schema from content
      // extractFAQSchema always returns an object with cleanedContent and faqSchema
      // Wrap in try-catch for extra safety
      let result;
      try {
        result = extractFAQSchema(actualDiary.content);
      } catch (extractError) {
        console.error('❌ [CAMP-DIARY] Error in extractFAQSchema:', extractError);
        // Return original content if extraction fails
        return {
          cleanedContent: actualDiary.content || '',
          faqSchema: null,
          faqItems: null
        };
      }
      
      // Ensure cleanedContent is always a string
      return {
        cleanedContent: result?.cleanedContent || actualDiary.content || '',
        faqSchema: result?.faqSchema || null,
        faqItems: result?.faqItems || null
      };
    } catch (error) {
      console.error('❌ [CAMP-DIARY] Error extracting FAQ schema:', error);
      // Return the original content if extraction fails
      const fallbackContent = actualDiary?.content || '';
      return { 
        cleanedContent: fallbackContent,
        faqSchema: null,
        faqItems: null
      };
    }
  }, [actualDiary?.content]);
  
  // Build structured data - MUST be called before early returns to maintain hook order
  // Use safe access with fallbacks to prevent errors
  const structuredData: any = useMemo(() => {
    if (!actualDiary) {
      return null;
    }
    
    try {
      // Ensure cleanedContent is a string before calling substring
      const contentPreview = cleanedContent && typeof cleanedContent === 'string' && cleanedContent.length > 0
        ? cleanedContent.substring(0, 160) + '...'
        : 'Camp diary entry';
      const description = actualDiary.excerpt || contentPreview;
      const imageUrl = actualDiary.featured_image_url 
        ? `${typeof window !== 'undefined' ? window.location.origin : 'https://campwhiz.com'}${actualDiary.featured_image_url}` 
        : undefined;
      
      return {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": actualDiary.title || 'Camp Diary',
        "description": description,
        "image": imageUrl,
        "author": {
          "@type": "Person",
          "name": actualDiary.author_name || "CampWhiz Community"
        },
        "publisher": {
          "@type": "Organization",
          "name": "CampWhiz",
          "logo": {
            "@type": "ImageObject",
            "url": "https://campwhiz.com/campwhiz-logo-color.svg"
          }
        },
        // Only include datePublished if the article is published
        // Use published_at if available, otherwise fall back to created_at for published articles
        ...(actualDiary.published ? {
          "datePublished": actualDiary.published_at || actualDiary.created_at
        } : {}),
        "dateModified": actualDiary.updated_at || actualDiary.published_at || actualDiary.created_at
      };
    } catch (error) {
      console.error('❌ [CAMP-DIARY] Error building structured data:', error);
      return null;
    }
  }, [actualDiary, cleanedContent]);

  // Debug logging to see what's happening - MUST be called before early returns
  useEffect(() => {
    if (actualDiary?.content) {
      console.log('📄 [CAMP-DIARY] ====== FAQ EXTRACTION DEBUG ======');
      console.log('📄 [CAMP-DIARY] Original content length:', actualDiary.content.length);
      console.log('📄 [CAMP-DIARY] Has "FAQPage" in content:', actualDiary.content.includes('"FAQPage"'));
      console.log('📄 [CAMP-DIARY] Cleaned content length:', cleanedContent.length);
      console.log('📄 [CAMP-DIARY] FAQ schema extracted:', !!faqSchema);
      console.log('📄 [CAMP-DIARY] Content changed?', actualDiary.content.length !== cleanedContent.length);
      console.log('📄 [CAMP-DIARY] Characters removed:', actualDiary.content.length - cleanedContent.length);
      if (faqSchema) {
        console.log('✅ [CAMP-DIARY] FAQ schema type:', faqSchema['@type']);
        console.log('✅ [CAMP-DIARY] FAQ schema questions:', faqSchema.mainEntity?.length || 0);
      } else {
        console.log('❌ [CAMP-DIARY] No FAQ schema was extracted');
        // Check if JSON might still be in cleaned content
        if (cleanedContent.includes('"FAQPage"')) {
          console.warn('⚠️ [CAMP-DIARY] WARNING: Cleaned content still contains "FAQPage"!');
          console.warn('⚠️ [CAMP-DIARY] This means extraction failed');
          // Log a snippet to help debug
          const faqIndex = cleanedContent.indexOf('"FAQPage"');
          console.warn('⚠️ [CAMP-DIARY] FAQPage found at index:', faqIndex);
          console.warn('⚠️ [CAMP-DIARY] Context around FAQPage:', cleanedContent.substring(Math.max(0, faqIndex - 100), Math.min(cleanedContent.length, faqIndex + 500)));
        }
      }
      console.log('📄 [CAMP-DIARY] ====================================');
    }
  }, [actualDiary?.content, cleanedContent, faqSchema]);

  // Admin action handlers
  const handleEditDiary = () => {
    if (!diary) return;
    navigate(`/admin/camp-diary/${diary.id}`);
  };

  const handleTogglePublished = async () => {
    if (!diary) return;
    
    try {
      const { error } = await supabase
        .from('camp_diaries')
        .update({ 
          published: !diary.published,
          published_at: !diary.published ? new Date().toISOString() : null
        })
        .eq('id', diary.id);

      if (error) throw error;

      toast.success(`Diary ${diary.published ? 'unpublished' : 'published'} successfully`);
      
      // Refresh the query - invalidate all variants (with and without adminStatus)
      // Use exact: false to match queries with additional parameters like adminStatus
      queryClient.invalidateQueries({ 
        queryKey: ['camp-diary', slug],
        exact: false
      });
    } catch (error) {
      console.error('Error toggling diary published status:', error);
      toast.error('Failed to update diary status');
    }
  };

  const handleDeleteDiary = () => {
    if (!diary) return;
    setDeleteConfirmation(diary);
  };

  const extractStoragePathFromUrl = (url: string): string | null => {
    try {
      // Supabase public URL format: https://[project].supabase.co/storage/v1/object/public/camp-diary-images/filename.jpg
      // We need to extract just the filename part
      const urlObj = new URL(url);
      const pathParts = urlObj.pathname.split('/');
      // Find the index of 'camp-diary-images' and get the next part
      const bucketIndex = pathParts.findIndex(part => part === 'camp-diary-images');
      if (bucketIndex !== -1 && bucketIndex < pathParts.length - 1) {
        // Get everything after 'camp-diary-images'
        return pathParts.slice(bucketIndex + 1).join('/');
      }
      // Fallback: try to extract from the end of the path
      const lastPart = pathParts[pathParts.length - 1];
      return lastPart || null;
    } catch (error) {
      console.warn('Failed to extract storage path from URL:', url, error);
      return null;
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmation) return;
    
    try {
      // Delete featured image from storage if it exists
      if (deleteConfirmation.featured_image_url) {
        try {
          const storagePath = extractStoragePathFromUrl(deleteConfirmation.featured_image_url);
          
          if (storagePath) {
            const { error: removeError } = await supabase.storage
              .from('camp-diary-images')
              .remove([storagePath]);

            if (removeError) {
              console.warn(`Warning: Failed to delete storage file ${storagePath}:`, removeError);
            }
          }
        } catch (storageError) {
          console.warn('Warning: Failed to delete image from storage:', storageError);
          // Continue with deletion even if storage cleanup fails
        }
      }

      // Delete diary from database
      const { error } = await supabase
        .from('camp_diaries')
        .delete()
        .eq('id', deleteConfirmation.id);

      if (error) throw error;

      toast.success('Diary deleted successfully');
      setDeleteConfirmation(null);
      
      // Navigate back to camp diaries list
      navigate('/camp-diaries');
    } catch (error) {
      console.error('Error deleting diary:', error);
      toast.error('Failed to delete diary');
    }
  };

  // During SSR, we MUST render something - even if it's an error state
  // This ensures the HTML source has actual content for SEO
  // Show loading only on client-side if we have no data AND no error
  if (!actualDiary && !error && typeof window !== 'undefined') {
    return (
      <div className="page-container">
        <div className="page-content">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        </div>
      </div>
    );
  }

  // During SSR, if we have no data, we should still render an error state
  // This ensures the HTML source has content (better than empty)
  // Always render SEO component (works for both SSR and client-side)
  if (error || !actualDiary) {
    return (
      <>
        <SEO
          title="Camp Diary | Camp Diaries | CampWhiz"
          description="Read inspiring camp stories and experiences from the CampWhiz community."
          url={`/camp-diaries/${slug}`}
          type="article"
        />
        <div className="page-container">
          <div className="page-content">
            <div className="text-center py-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Story not found</h2>
              <p className="text-gray-600 mb-6">The camp diary you're looking for doesn't exist or has been removed.</p>
              <Link
                to="/camp-diaries"
                className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors"
              >
                Browse All Stories
              </Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Check if this is a preview (unpublished diary viewed by admin)
  const isPreview = adminStatus && !actualDiary.published;

  return (
    <>
      <SEO
        title={`${actualDiary.title || 'Camp Diary'} | Camp Diaries | CampWhiz`}
        description={
          actualDiary.excerpt || 
          (cleanedContent && typeof cleanedContent === 'string' && cleanedContent.length > 0
            ? cleanedContent.substring(0, 160) + '...'
            : 'Camp diary entry')
        }
        url={`/camp-diaries/${actualDiary.slug}`}
        type="article"
        image={actualDiary.featured_image_url || '/campwhiz-ogimage.png'}
        structuredData={
          structuredData 
            ? (faqSchema ? [structuredData, faqSchema] : structuredData)
            : undefined
        }
        noindex={isPreview} // Don't index unpublished/preview pages
      />

      <article className="page-container" itemScope itemType="https://schema.org/BlogPosting">
        <div className="page-content">
          <div className="max-w-4xl mx-auto">
            {/* Preview Banner for Admins viewing unpublished diaries */}
            {isPreview && (
              <aside className="mb-6 bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-md" role="alert">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700 font-medium">
                      <strong>Preview Mode:</strong> This diary is not published and is only visible to admins.
                    </p>
                  </div>
                </div>
              </aside>
            )}

            {/* Admin Actions */}
            {adminStatus && (
              <AdminActionsBar
                title="Admin Actions"
                subtitle="Manage this camp diary"
                actions={[
                  {
                    label: 'Edit Diary',
                    icon: PencilSquareIcon,
                    onClick: handleEditDiary
                  },
                  {
                    label: actualDiary.published ? 'Unpublish' : 'Publish',
                    onClick: handleTogglePublished
                  },
                  {
                    label: 'Delete Diary',
                    onClick: handleDeleteDiary,
                    variant: 'danger' as const
                  }
                ]}
                isAdmin={adminStatus}
              />
            )}

            {/* Breadcrumb */}
            <nav className="hidden lg:block mb-8" aria-label="Breadcrumb">
              <ol className="flex items-center space-x-2 text-sm text-gray-500" itemScope itemType="https://schema.org/BreadcrumbList">
                <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                  <Link to="/camp-diaries" className="text-primary hover:text-primary/80" itemProp="item">
                    <span itemProp="name">Camp Diaries</span>
                  </Link>
                  <meta itemProp="position" content="1" />
                </li>
                <ChevronRightIcon className="h-4 w-4" />
                <li className="text-gray-900 font-medium" itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                  <span itemProp="name">{actualDiary.title}</span>
                  <meta itemProp="position" content="2" />
                </li>
              </ol>
            </nav>

            {/* Article Header */}
            <header className="mb-8">
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold text-gray-900 mb-4" itemProp="headline">
                {actualDiary.title}
              </h1>
              {actualDiary.excerpt && (
                <p className="text-xl text-gray-600 mb-6" itemProp="description">{actualDiary.excerpt}</p>
              )}
              {actualDiary.featured_image_url && (
                <figure className="mb-6">
                  <img
                    src={actualDiary.featured_image_url}
                    alt={actualDiary.title}
                    className="w-full aspect-video object-contain rounded-lg bg-gray-100"
                    itemProp="image"
                  />
                </figure>
              )}
              <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500">
                {actualDiary.author_name && (
                  <div className="flex items-center" itemProp="author" itemScope itemType="https://schema.org/Person">
                    <UserIcon className="h-5 w-5 mr-2" aria-hidden="true" />
                    <span itemProp="name">{actualDiary.author_name}</span>
                  </div>
                )}
                {actualDiary.published_at && (
                  <div className="flex items-center">
                    <CalendarIcon className="h-5 w-5 mr-2" aria-hidden="true" />
                    <time
                      dateTime={actualDiary.published_at}
                      itemProp="datePublished"
                    >
                      {new Date(actualDiary.published_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </time>
                    <meta itemProp="dateModified" content={actualDiary.updated_at || actualDiary.published_at} />
                  </div>
                )}
                {actualDiary.tags && actualDiary.tags.length > 0 && (
                  <div className="flex items-center flex-wrap gap-2">
                    <TagIcon className="h-5 w-5" aria-hidden="true" />
                    {actualDiary.tags.map((tag) => (
                      <Link
                        key={tag}
                        to={`/camp-diaries?tag=${encodeURIComponent(tag)}`}
                        className="px-3 py-1 bg-white text-primary border-2 border-primary rounded-full hover:bg-primary/5 transition-colors"
                        itemProp="keywords"
                      >
                        {tag}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </header>

            {/* Article Content */}
            <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
              <div itemProp="articleBody">
                {cleanedContent && cleanedContent.trim() ? (
                  <FAQContentRenderer
                    content={cleanedContent}
                    faqItems={faqItems}
                  />
                ) : (
                  <div className="text-gray-500 italic">
                    <p>No content available for this diary entry.</p>
                  </div>
                )}
              </div>
              <meta itemProp="publisher" itemScope itemType="https://schema.org/Organization" content="CampWhiz" />
            </section>

            {/* Navigation Footer */}
            <footer className="mt-12 text-center">
              <nav>
                <Link
                  to="/camp-diaries"
                  className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors"
                >
                  ← Back to Camp Diaries
                </Link>
              </nav>
            </footer>
          </div>
        </div>
      </article>

      {/* Delete Confirmation Modal */}
      {deleteConfirmation && (
        <DeleteConfirmationModal
          isOpen={!!deleteConfirmation}
          onClose={() => setDeleteConfirmation(null)}
          onConfirm={handleDeleteConfirm}
          title="Delete Camp Diary"
          message={`Are you sure you want to delete "${deleteConfirmation.title}"? This action cannot be undone.`}
        />
      )}
    </>
  );
}
